import { Box, CircularProgress, Dialog, Grid, Typography } from "@mui/material";
import { EDecimalToHex, sphereColors } from "@styles/common-colors";
import { AttachmentItemActions } from "@pages/project-details/project-markups/sidepanel/attachment/attachment-item-actions";
import { FaroIconButton } from "@components/common/faro-icon-button";
import { IElementAttachment } from "@faro-lotv/ielement-types";
import { addTransparency } from "@utils/ui-utils";
import { CommonStyles, withEllipsis } from "@styles/common-styles";
import CloseIcon from "@assets/icons/new/close_24px.svg?react";
import { ReactSetStateFunction } from "@custom-types/types";
import { useRefreshAttachment } from "@pages/project-details/project-markups/sidepanel/use-refresh-attachment";
import { useEffect, useState } from "react";

interface Props {
  /** The attachment item to display */
  attachment: IElementAttachment;

  /** Check if is downloading the image */
  isDownloading: boolean;

  /** Function to toggle the preview dialog visibility */
  setIsPreviewDialogOpen: (value: boolean) => void;

  /** Function to update the downloading state of the attachment */
  setIsDownloading: (value: boolean) => void;

  /** Function to set the selected attachments */
  setSelectedAttachments: ReactSetStateFunction<Set<string>>;
}

/** Size in pixels for each attachment action */
const ACTION_BTN_SIZE = "1.5rem";

/** Size in pixels for each attachment action icon */
const ACTION_ICON_SIZE = "1.5rem";

/**
 * This component renders a full-screen dialog to preview an attachment. It displays the attachment's image,
 * name, and provides actions for downloading or deleting the attachment.
 */
export function FaroImagePreview({
  attachment,
  isDownloading,
  setIsPreviewDialogOpen,
  setIsDownloading,
  setSelectedAttachments,
}: Props): JSX.Element {
  const { refreshAttachmentIfExpired, isAttachmentExpired } =
    useRefreshAttachment();

  const [currentAttachment, setCurrentAttachment] =
    useState<IElementAttachment>(attachment);
  const [isImageLoading, setIsImageLoading] = useState<boolean>(false);

  useEffect(() => {
    async function checkAndUpdateAttachment(): Promise<void> {
      try {
        if (isAttachmentExpired(attachment)) {
          setIsImageLoading(true);

          const updatedAttachment = await refreshAttachmentIfExpired([
            attachment.id,
          ]);
          setCurrentAttachment(updatedAttachment[0]);
          setIsImageLoading(false);
        }
      } catch {
        setIsImageLoading(false);
      }
    }

    void checkAndUpdateAttachment();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- The empty array ensures it runs only once, on mount
  }, []);

  return (
    <Dialog
      open={true}
      fullScreen
      PaperProps={{
        sx: {
          backgroundColor: "transparent",
        },
      }}
    >
      <Box
        data-testid="preview-attachment-dialog-content"
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            height: "70px",
            alignItems: "center",
            boxSizing: "border-box",
            borderBottom: CommonStyles.Borders.gray200Divider,
            backgroundColor: sphereColors.pureWhite,
            zIndex: 1,
            position: "relative",
          }}
        >
          <Grid
            container
            alignItems="center"
            justifyContent="space-between"
            sx={{
              px: "1rem",
            }}
          >
            <Grid item xs zeroMinWidth>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  overflow: "hidden",
                }}
              >
                <FaroIconButton
                  dataTestId="preview-dialog-close-icon"
                  component={CloseIcon}
                  buttonSize={ACTION_BTN_SIZE}
                  iconSize={ACTION_ICON_SIZE}
                  iconButtonProps={{
                    sx: {
                      padding: "20px",
                    },
                  }}
                  onClick={() => setIsPreviewDialogOpen(false)}
                />
                <Typography
                  sx={{
                    marginLeft: "10px",
                    fontWeight: "700",
                    ...withEllipsis,
                  }}
                  title={currentAttachment.name}
                >
                  {currentAttachment.name}
                </Typography>
              </Box>
            </Grid>
            <Grid item>
              <Box
                sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}
              >
                {isDownloading ? (
                  <CircularProgress size={"1rem"} />
                ) : (
                  <AttachmentItemActions
                    attachment={currentAttachment}
                    setIsDownloading={setIsDownloading}
                    eventSource="preview attachment"
                    buttonSize={ACTION_BTN_SIZE}
                    iconSize={ACTION_ICON_SIZE}
                    iconBtnProps={{
                      sx: {
                        padding: "20px",
                      },
                    }}
                    setSelectedAttachments={setSelectedAttachments}
                  />
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flex: 1,
            padding: { xs: "16px", md: "24px" },
            backgroundColor: addTransparency({
              color: sphereColors.gray800,
              alpha: EDecimalToHex.hundredFortyFour,
            }),
            backdropFilter: "blur(15px)",
            overflow: "auto",
          }}
        >
          {isImageLoading ? (
            <CircularProgress
              size={"2rem"}
              sx={{
                color: sphereColors.pureWhite,
              }}
            />
          ) : (
            <Box
              component="img"
              src={currentAttachment.signedUri}
              alt="attachment annotation"
              sx={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
                width: "auto",
                height: "auto",
              }}
            />
          )}
        </Box>
      </Box>
    </Dialog>
  );
}
